import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import "./style.css";
import {AdminLayout} from "../../layouts/admin-layout";
import {addressTypeConfigs, addressGroupConfigs, AddressType} from "../../webConstants";
import {setStaticAddresses, getStaticAddresses, StaticAddressSetData} from "../../services/userService";
import {isAxiosError} from "axios";

type AddressFormData = {
    address: string;
    generation: boolean;
};

type FormData = {
    [key: number]: AddressFormData;
};

type PatternData = {
    [key: number]: string;
};

type AddressTypeFieldProps = {
    config: { type: AddressType; label: string; placeholder: string; group: string; icon: React.ReactElement };
    control: any;
    pattern: string;
};

function AddressTypeField({ config, control, pattern }: AddressTypeFieldProps) {
    const addressType = config.type;
    const generationAvailable = pattern !== "";

    return (
        <div className="p-4 rounded-xl bg-black/20 border border-white/5 flex flex-col gap-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                {config.icon}
                <span>{config.label}</span>
            </label>
            <Controller
                name={`${addressType}.address`}
                control={control}
                defaultValue=""
                render={({ field }) => (
                    <input
                        {...field}
                        className="glass-input w-full rounded-lg px-4 py-2.5 text-sm font-mono placeholder-slate-600"
                        placeholder={config.placeholder}
                        type="text"
                    />
                )}
            />
            <div className="flex flex-wrap items-center gap-4 pt-1">
                <Controller
                    name={`${addressType}.generation`}
                    control={control}
                    defaultValue={false}
                    render={({ field: { value, onChange } }) => (
                        <div className="inline-flex items-center gap-2">
                            <label className={`inline-flex items-center group/toggle ${generationAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                <input
                                    className="sr-only peer"
                                    type="checkbox"
                                    checked={generationAvailable ? value : false}
                                    onChange={(e) => onChange(e.target.checked)}
                                    disabled={!generationAvailable}
                                />
                                <div className={`relative w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 transition-colors ${!generationAvailable ? 'peer-disabled:opacity-50' : ''}`}></div>
                                <span className={`ms-3 text-xs font-medium transition-colors ${generationAvailable ? 'text-slate-400 group-hover/toggle:text-slate-300' : 'text-slate-500'}`}>
                                    {generationAvailable ? 'Enable Generation' : 'Generation unavailable'}
                                </span>
                            </label>
                            {generationAvailable && (
                                <div className="relative group/tooltip">
                                    <span className="material-symbols-outlined text-slate-500 hover:text-slate-300 cursor-help text-base transition-colors">help</span>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 border border-white/10 rounded-lg text-xs text-slate-300 whitespace-nowrap opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-10 shadow-lg">
                                        Generation Pattern: {pattern}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                />
            </div>
        </div>
    );
}


export function SettingsPage() {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
    const [patterns, setPatterns] = useState<PatternData>({});
    const detailsRefs = useRef<{ [key: string]: HTMLDetailsElement | null }>({});

    const { control, handleSubmit, watch, reset, formState: { isSubmitting } } = useForm<FormData>({
        defaultValues: addressTypeConfigs.reduce((acc, config) => {
            acc[config.type] = {
                address: "",
                generation: false,
            };
            return acc;
        }, {} as FormData)
    });

    useEffect(() => {
        let mounted = true;

        const loadSettings = async () => {
            setLoading(true);
            setLoadError(null);

            try {
                const response = await getStaticAddresses();

                if (!mounted) return;

                const formData: FormData = {};
                const patternData: PatternData = {};

                addressTypeConfigs.forEach(config => {
                    const addressData = response.static_addresses.find(
                        addr => addr.type_id === config.type
                    );

                    formData[config.type] = addressData ? {
                        address: addressData.address || "",
                        generation: addressData.generation,
                    } : {
                        address: "",
                        generation: false,
                    };

                    patternData[config.type] = addressData?.pattern || "";
                });

                reset(formData);
                setInitialFormData(formData);
                setPatterns(patternData);
            } catch (error) {
                if (!mounted) return;

                if (isAxiosError(error)) {
                    setLoadError(error.response?.data?.message || "Failed to load settings. Please try again.");
                } else {
                    setLoadError("An unexpected error occurred. Please try again.");
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadSettings();

        return () => {
            mounted = false;
        };
    }, [reset]);

    const groupedConfigs = addressGroupConfigs.map(groupConfig => ({
        groupConfig,
        configs: addressTypeConfigs.filter(config => config.group === groupConfig.name)
    }));

    const onSubmit = async (data: FormData) => {
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const addresses: StaticAddressSetData[] = Object.entries(data).map(([typeId, addressData]) => ({
                type_id: parseInt(typeId),
                address: addressData.address || "",
                generation: addressData.generation,
            }));

            const response = await setStaticAddresses(addresses);

            if (response.status === "updated") {
                setSubmitSuccess(true);
                setTimeout(() => setSubmitSuccess(false), 3000);
                // Обновляем исходные данные после успешного сохранения
                setInitialFormData(data);
            } else {
                setSubmitError("Failed to save settings. Invalid response from server.");
            }
        } catch (error) {
            if (isAxiosError(error)) {
                setSubmitError(error.response?.data?.message || "Failed to save settings. Please try again.");
            } else {
                setSubmitError("An unexpected error occurred. Please try again.");
            }
        }
    };

    const handleExpandAll = () => {
        Object.values(detailsRefs.current).forEach(details => {
            if (details) {
                details.open = true;
            }
        });
    };

    const handleCollapseAll = () => {
        Object.values(detailsRefs.current).forEach(details => {
            if (details) {
                details.open = false;
            }
        });
    };

    const currentFormData = watch();
    const hasChanges = initialFormData !== null && JSON.stringify(currentFormData) !== JSON.stringify(initialFormData);

    // Предупреждение при попытке уйти со страницы с несохраненными изменениями
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Перехватываем клики по ссылкам для предупреждения при внутренней навигации
        const handleLinkClick = (e: MouseEvent) => {
            if (hasChanges) {
                const target = e.target as HTMLElement;
                const link = target.closest('a[href]');
                if (link) {
                    const href = link.getAttribute('href');
                    if (href && (href.startsWith('/') || href.startsWith('#'))) {
                        const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave? Your data will not be saved.');
                        if (!confirmed) {
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                            return false;
                        }
                    }
                }
            }
        };

        document.addEventListener('click', handleLinkClick, true);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleLinkClick, true);
        };
    }, [hasChanges]);

    return (
        <AdminLayout>
            <div className="w-full max-w-4xl flex flex-col gap-6 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-2">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-3xl font-bold tracking-tight text-white drop-shadow-sm">Address Generation</h2>
                        <p className="text-slate-400">Configure parameters for cryptocurrency address generation.</p>
                    </div>
                    {!loading && !loadError && (
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleExpandAll}
                                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/5">
                                Expand All
                            </button>
                            <button
                                onClick={handleCollapseAll}
                                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/5">
                                Collapse All
                            </button>
                            <button
                                onClick={handleSubmit(onSubmit)}
                                disabled={isSubmitting || !hasChanges}
                                className="px-4 py-2 rounded-lg bg-primary hover:bg-blue-600 text-white text-sm font-medium transition-colors shadow-lg shadow-primary/25 border border-transparent disabled:opacity-50 disabled:cursor-not-allowed">
                                {isSubmitting ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="glass-panel rounded-2xl border border-slate-500/30 bg-slate-500/10 p-4 text-sm text-slate-300 text-center">
                        <p>Loading settings...</p>
                    </div>
                )}

                {loadError && (
                    <div className="glass-panel rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                        {loadError}
                    </div>
                )}

                {!loading && !loadError && (
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
                        {submitSuccess && (
                            <div className="glass-panel rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-200">
                                Settings saved successfully!
                            </div>
                        )}

                        {submitError && (
                            <div className="glass-panel rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                                {submitError}
                            </div>
                        )}

                        {groupedConfigs.map(({ groupConfig, configs }) => {
                            return (
                                <details
                                    key={groupConfig.name}
                                    ref={(el) => {
                                        if (el) {
                                            detailsRefs.current[groupConfig.name] = el;
                                        }
                                    }}
                                    className="group glass-panel rounded-2xl overflow-hidden"
                                >
                                    <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-white/5 transition-colors select-none">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg ${groupConfig.bgColor} flex items-center justify-center ${groupConfig.color} border`}>
                                                {groupConfig.icon}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-white text-lg">{groupConfig.displayName}</h3>
                                                <p className="text-xs text-slate-400">{groupConfig.description}</p>
                                            </div>
                                        </div>
                                        <span className="material-symbols-outlined transition-transform duration-300 group-open:rotate-180 text-white/50 text-2xl">expand_more</span>
                                    </summary>
                                    <div className="p-6 pt-0 flex flex-col gap-4 border-t border-white/5 mt-2">
                                        {configs.map((config, index) => (
                                            <div key={config.type} className={index === 0 ? "mt-4" : ""}>
                                                <AddressTypeField
                                                    config={config}
                                                    control={control}
                                                    pattern={patterns[config.type] || ""}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            );
                        })}
                    </form>
                )}
            </div>
        </AdminLayout>
    )
}
